import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Button, Col, Form, Input, Row, message } from "antd";
import { connect } from "dva";
import { SecurePassAutoSend } from "@/utils/enum";
import { Validator } from "@/utils/validator";
import { updateAddress } from "@/services/addresses";
import AddressInput from "@/components/AddressInput";
import PhoneNumberInput, { flatPhoneNumber } from "@/components/PhoneNumberInput";
import SecurePassDropdown from "@/components/SecurePass/SecurePassDropdown/SecurePassDropdown";
import ShipmentRestrictedDaysFormItem from "@/components/ShipmentRestrictedDaysFormItem";
import initialValues from "./constants/initialValues";

const { TextArea } = Input;
const isMobile = window.innerWidth <= 768;

const EditAddressForm = ({ configLang, item, onClickCancel, onEditSuccess, securePass }) => {
  const editAddressFormRef = useRef();
  const [submitting, setSubmitting] = useState(false);

  const isSecurePassDropdownVisible = useMemo(
    () =>
      !!securePass?.enabled &&
      !!item?.dealer?.autosendType &&
      item?.dealer?.autosendType !== SecurePassAutoSend.NOT_SEND,
    [item?.dealer?.autosendType, securePass?.enabled],
  );

  useEffect(() => {
    if (!item || !editAddressFormRef?.current) return;

    // Scroll to top of address drawer to ensure form is always displayed from top
    const drawer = document.querySelector(".ant-drawer-body");
    drawer?.scrollTo(0, 0);

    /**
     * @type {Address}
     */
    const addressData = item;

    const {
      name: locationName,
      addressLine1,
      addressLine2,
      city,
      stateId,
      zipCode,
      contactEmail,
      contactName,
      contactPhone,
      pickupNotes,
      dropOffNotes,
      dayOfWeekRestricted,
      stateName,
      stateShortName,
      autosendType,
    } = addressData;

    editAddressFormRef?.current?.setFieldsValue({
      locationName,
      addressLine1,
      addressLine2,
      city,
      stateId,
      zipCode,
      contactEmail,
      contactName,
      contactPhone,
      pickupNotes,
      dropOffNotes,
      stateName,
      stateShortName,
      dayOfWeekRestricted: dayOfWeekRestricted?.split(",") ?? [],
      autosendType,
    });
  }, [item]);

  const closeForm = useCallback(() => {
    onClickCancel();
  }, [onClickCancel]);

  const onSubmit = useCallback(
    async values => {
      /**
       * @type {EditAddressRequestData}
       */
      let editAddressRequestData = {};

      const parseAddressData = ({
        locationName,
        addressLine1,
        addressLine2,
        city,
        stateId,
        zipCode,
        contactEmail,
        contactPhone,
        contactPhoneExtension,
        contactName,
        pickupNotes,
        dropOffNotes,
        dayOfWeekRestricted,
        autosendType,
      }) => {
        const parsedAddress = {
          name: locationName,
          addressLine1,
          addressLine2,
          city,
          stateId,
          zipCode,
          contactEmail,
          contactPhone: flatPhoneNumber(contactPhone),
          contactPhoneExtension,
          contactName,
          pickupNotes,
          dropOffNotes,
          dayOfWeekRestricted: dayOfWeekRestricted?.sort?.()?.toString?.() ?? null,
          autosendType: isSecurePassDropdownVisible ? autosendType : SecurePassAutoSend.NOT_SEND,
        };

        return parsedAddress;
      };

      editAddressRequestData = parseAddressData(values);
      setSubmitting(true);

      let res;
      try {
        res = await updateAddress(item.id, editAddressRequestData);

        if (res.error) {
          throw new Error();
        } else {
          // To ensure that the submission state set in "finally" runs ok
          message.success("Successfully edited address");
          closeForm();

          if (typeof onEditSuccess === "function") {
            onEditSuccess();
          }
        }
      } catch (err) {
        message.error("Error saving changes");
        console.error(err);
      } finally {
        setSubmitting(false);
      }
    },
    [item?.id, closeForm, onEditSuccess, isSecurePassDropdownVisible],
  );

  const onSubmitError = useCallback(() => {
    message.error("Form is missing required fields");
  }, []);

  return (
    <Form
      id={item.id}
      ref={editAddressFormRef}
      layout="vertical"
      initialValues={initialValues}
      onFinish={onSubmit}
      onFinishFailed={onSubmitError}
    >
      <Row justify="space-between">
        <Col>
          <div style={{ fontWeight: "bold", fontSize: 20 }}>{configLang.CoreService?.EditAddress}</div>
        </Col>

        <Col>
          <Button size="small" htmlType="submit" type="primary" style={{ marginRight: 5 }} loading={submitting}>
            {configLang.CoreService?.Save}
          </Button>
          <Button size="small" onClick={onClickCancel} type="secondary">
            {configLang.CoreService?.Cancel}
          </Button>
        </Col>
      </Row>

      <br />
      <Row gutter={16}>
        <Col flex={1}>
          <Form.Item
            name="locationName"
            label={configLang.CoreService?.LocationName}
            rules={[Validator.required("Please enter a location name")]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24}>
          <AddressInput formReference={editAddressFormRef} />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={12}>
          <Form.Item
            name="contactName"
            label={configLang.CoreService?.ContactName}
            rules={[Validator.required("Please enter a contact name")]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={12}>
          <PhoneNumberInput
            formItemProps={{
              name: "contactPhone",
              label: configLang.CoreService?.ContactPhone,
              formRef: editAddressFormRef,
              rules: [Validator.required("Please enter a contact phone number")],
            }}
          />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col flex={1}>
          <Form.Item name="contactEmail" label="Contact Email" hidden>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col flex={1}>
          <Form.Item name="pickupNotes" label={configLang.CoreService?.SavedPickupNotes}>
            <TextArea placeholder={configLang.CoreService?.SavedNotesPlaceholder} rows={4} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col flex={1}>
          <Form.Item name="dropOffNotes" label={configLang.CoreService?.SavedDropOffNotes}>
            <TextArea placeholder={configLang.CoreService?.SavedNotesPlaceholder} rows={4} />
          </Form.Item>
        </Col>
      </Row>
      {isSecurePassDropdownVisible && (
        <Row gutter={16}>
          <Col flex={1}>
            <SecurePassDropdown label="Use SecurePass" name="autosendType" />
          </Col>
        </Row>
      )}
      <Row gutter={16}>
        <Col flex={1}>
          <ShipmentRestrictedDaysFormItem name="dayOfWeekRestricted" />
        </Col>
      </Row>

      {isMobile && (
        <Button size="small" htmlType="submit" type="primary" style={{ marginRight: 5 }} loading={submitting} block>
          {configLang.CoreService?.Save}
        </Button>
      )}
    </Form>
  );
};

export default connect(({ configs }) => ({
  configLang: configs.configLang,
  securePass: configs.securePass,
}))(EditAddressForm);
